import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Box,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

export interface ExportModule {
  id: string;
  label: string;
  hasData: boolean;
  description?: string;
}

interface ExportReportModalProps {
  open: boolean;
  modules: ExportModule[];
  onExport: (selectedModuleIds: string[]) => Promise<void>;
  onClose: () => void;
  isExporting?: boolean;
}

const ExportReportModal: React.FC<ExportReportModalProps> = ({
  open,
  modules,
  onExport,
  onClose,
  isExporting = false,
}) => {
  const [selected, setSelected] = React.useState<Set<string>>(
    new Set(modules.filter((m) => m.hasData).map((m) => m.id)),
  );
  const [error, setError] = React.useState<string | null>(null);
  const wasOpenRef = React.useRef(false);

  React.useEffect(() => {
    if (open && !wasOpenRef.current) {
      setSelected(new Set(modules.filter((m) => m.hasData).map((m) => m.id)));
      setError(null);
    }
    wasOpenRef.current = open;
  }, [open, modules]);

  const handleToggleModule = (moduleId: string) => {
    const newSelected = new Set(selected);
    if (newSelected.has(moduleId)) {
      newSelected.delete(moduleId);
    } else {
      newSelected.add(moduleId);
    }
    setSelected(newSelected);
  };

  const handleExport = async () => {
    if (selected.size === 0) {
      setError("Please select at least one module to export.");
      return;
    }

    try {
      setError(null);
      await onExport(Array.from(selected));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to export report");
    }
  };

  const modulesWithData = modules.filter((m) => m.hasData);
  const modulesWithoutData = modules.filter((m) => !m.hasData);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, color: "#8C1515" }}>
        📥 Export Report
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2}>
          {modulesWithData.length === 0 ? (
            <Alert severity="warning">
              No modules have been executed yet. Please run at least one module
              before exporting.
            </Alert>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Select modules to include ({selected.size} selected):
              </Typography>

              {modulesWithData.length > 0 && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Available Modules:
                  </Typography>
                  <FormGroup sx={{ mt: 1 }}>
                    {modulesWithData.map((module) => (
                      <FormControlLabel
                        key={module.id}
                        control={
                          <Checkbox
                            checked={selected.has(module.id)}
                            onChange={() => handleToggleModule(module.id)}
                            disabled={isExporting}
                          />
                        }
                        label={
                          <Stack spacing={0.5}>
                            <Typography variant="body2">
                              {module.label}
                            </Typography>
                            {module.description && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {module.description}
                              </Typography>
                            )}
                          </Stack>
                        }
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}

              {modulesWithoutData.length > 0 && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Unavailable (not run):
                  </Typography>
                  <FormGroup sx={{ mt: 1, opacity: 0.5 }}>
                    {modulesWithoutData.map((module) => (
                      <FormControlLabel
                        key={module.id}
                        control={<Checkbox disabled />}
                        label={
                          <Typography variant="body2" color="textSecondary">
                            {module.label}
                          </Typography>
                        }
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  💾 Your report will be exported as a ZIP file containing:
                </Typography>
                <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                  <li>
                    <Typography variant="caption">
                      Separate JSON files for each selected module
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="caption">
                      An interactive HTML summary report
                    </Typography>
                  </li>
                </ul>
              </Alert>

              {error && <Alert severity="error">{error}</Alert>}
            </>
          )}

          {isExporting && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Stack alignItems="center" spacing={1}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="textSecondary">
                  Generating report...
                </Typography>
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isExporting}>
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          startIcon={<FileDownloadIcon />}
          disabled={
            isExporting || selected.size === 0 || modulesWithData.length === 0
          }
          sx={{
            backgroundColor: "#8C1515",
            "&:hover": { backgroundColor: "#B33A3A" },
          }}
        >
          {isExporting ? "Exporting..." : "Download Report"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportReportModal;
